import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "./AdminDashboard.css";

const API = process.env.REACT_APP_API_URL || "/api";
const ax = (method, url, data, params) =>
  axios({ method, url: `${API}${url}`, data, params, withCredentials: true });

/* ─── tiny helpers ─────────────────────────────────────────── */
const uid = () => Math.random().toString(36).slice(2, 9);

const EMPTY_RESOURCE = () => ({
  _uid: uid(), type: "playlist", label: "", title: "", url: "", estimatedMinutes: 60,
});

const EMPTY_PROJECT = () => ({
  title: "", description: "", requiredSkills: [], exampleUrl: "",
});

const EMPTY_PHASE = (order = 1) => ({
  _uid: uid(),
  title: `Phase ${order}: `,
  description: "",
  order,
  readinessWeight: 20,
  unlocksAfterPrevious: order > 1,
  assessmentDifficulty: "hard",
  assessmentVideoIds: [],
  assessmentGenerated: false,
  resources: [EMPTY_RESOURCE()],
  project: EMPTY_PROJECT(),
});

const EMPTY_TRACK = () => ({
  title: "", slug: "", description: "",
  audience: "college", goal: "internship", category: "frontend",
  thumbnail: "", estimatedMonths: 5, isPro: false,
  freePhaseCount: 1, isPublished: false, tags: [],
  prerequisites: [],
  phases: [EMPTY_PHASE(1)],
});

const TABS = ["overview", "phases", "settings"];
const RESOURCE_TYPES = ["playlist", "video", "article", "docs", "pdf"];
const AUDIENCES = ["college", "exam", "general", "hardware"];
const GOALS = ["internship", "job", "exam", "skill", "project"];
const CATEGORIES = ["frontend", "backend", "fullstack", "python", "vlsi", "hardware", "exam", "other"];

// const fmtDuration = (secs) => {
//   if (!secs) return "0h";
//   const h = Math.floor(secs / 3600);
//   const m = Math.floor((secs % 3600) / 60);
//   return h > 0 ? `${h}h ${m}m` : `${m}m`;
// };

/* ============================================================
   § ResourceRow
   ============================================================ */
function ResourceRow({ res, onChange, onDelete }) {
  return (
    <div className="ad-resource-row">
      <select value={res.type} onChange={(e) => onChange({ ...res, type: e.target.value })} className="ad-select-sm">
        {RESOURCE_TYPES.map((t) => <option key={t}>{t}</option>)}
      </select>
      <input className="ad-input ad-input--grow" placeholder="Label (Curated)" value={res.label}
        onChange={(e) => onChange({ ...res, label: e.target.value })} />
      <input className="ad-input ad-input--grow" placeholder="Essential Title" value={res.title || ""}
        onChange={(e) => onChange({ ...res, title: e.target.value })} />
      <input className="ad-input ad-input--grow" placeholder="URL" value={res.url || ""}
        onChange={(e) => onChange({ ...res, url: e.target.value })} />
      <input className="ad-input ad-input--sm" type="number" min={0} placeholder="mins"
        value={res.estimatedMinutes || 0}
        onChange={(e) => onChange({ ...res, estimatedMinutes: Number(e.target.value) })} />
      {res.courseId && (
        <span className="ad-res-course-badge" title={`Linked to Course: ${res.courseId}`}>📦</span>
      )}
      <button className="ad-icon-btn ad-icon-btn--danger" onClick={onDelete} title="Remove">×</button>
    </div>
  );
}

/* ============================================================
   § QuizStatusBadge
   ============================================================ */
function QuizStatusBadge({ phase, quizStatus }) {
  const phaseStatus = quizStatus?.phases?.find((p) => p.order === phase.order);
  const videoCount = phase.assessmentVideoIds?.length || 0;

  if (videoCount === 0) return <span className="ad-quiz-badge ad-quiz-badge--none">No Assessment</span>;
  if (!phaseStatus) return <span className="ad-quiz-badge ad-quiz-badge--unknown">?</span>;
  if (phaseStatus.allGenerated) return <span className="ad-quiz-badge ad-quiz-badge--done">✓ Quizzes Ready</span>;

  const doneCount = phaseStatus.quizzes?.filter((q) => q.exists).length || 0;
  return (
    <span className="ad-quiz-badge ad-quiz-badge--partial">
      {doneCount}/{videoCount} Quizzes
    </span>
  );
}

/* ============================================================
   § PhaseCard
   ============================================================ */
function PhaseCard({ phase, idx, total, onChange, onDelete, onMove, quizStatus, onGenerateQuiz, generatingQuiz }) {
  const [open, setOpen] = useState(idx === 0);

  const setField = (key, val) => onChange({ ...phase, [key]: val });
  const setResource = (i, val) => {
    const resources = [...phase.resources]; resources[i] = val;
    onChange({ ...phase, resources });
  };
  const deleteResource = (i) => onChange({ ...phase, resources: phase.resources.filter((_, j) => j !== i) });
  const addResource = () => onChange({ ...phase, resources: [...phase.resources, EMPTY_RESOURCE()] });
  const setProject = (key, val) => onChange({ ...phase, project: { ...(phase.project || EMPTY_PROJECT()), [key]: val } });
  const setAssessmentIds = (raw) => {
    const ids = raw.split(/[\n,\s]+/).map((s) => s.trim()).filter(Boolean);
    setField("assessmentVideoIds", ids);
  };

  const phaseQuizStatus = quizStatus?.phases?.find((p) => p.order === phase.order);
  const videoCount = phase.assessmentVideoIds?.length || 0;
  const allReady = phaseQuizStatus?.allGenerated;

  return (
    <div className={`ad-phase-card ${open ? "ad-phase-card--open" : ""}`}>
      <div className="ad-phase-header" onClick={() => setOpen(!open)}>
        <div className="ad-phase-header-left">
          <span className="ad-phase-badge">P{phase.order}</span>
          <span className="ad-phase-title-preview">{phase.title || `Phase ${phase.order}`}</span>
          <span className="ad-phase-meta">{phase.resources?.length || 0} res · {phase.readinessWeight}%</span>
          <QuizStatusBadge phase={phase} quizStatus={quizStatus} />
        </div>
        <div className="ad-phase-header-right">
          {videoCount > 0 && !allReady && (
            <button
              className="ad-icon-btn ad-icon-btn--quiz"
              disabled={generatingQuiz}
              onClick={(e) => { e.stopPropagation(); onGenerateQuiz(phase.order); }}
              title="Generate quizzes for this phase"
            >
              {generatingQuiz ? "⏳" : "🤖"}
            </button>
          )}
          <button className="ad-icon-btn" disabled={idx === 0}
            onClick={(e) => { e.stopPropagation(); onMove(idx, -1); }} title="Move up">↑</button>
          <button className="ad-icon-btn" disabled={idx === total - 1}
            onClick={(e) => { e.stopPropagation(); onMove(idx, 1); }} title="Move down">↓</button>
          <button className="ad-icon-btn ad-icon-btn--danger"
            onClick={(e) => { e.stopPropagation(); onDelete(); }} title="Delete">×</button>
          <span className="ad-phase-chevron">{open ? "▲" : "▼"}</span>
        </div>
      </div>

      {open && (
        <div className="ad-phase-body">
          <div className="ad-field-row">
            <div className="ad-field ad-field--grow">
              <label>Phase Title</label>
              <input className="ad-input" value={phase.title}
                onChange={(e) => setField("title", e.target.value)} placeholder="Phase title..." />
            </div>
            <div className="ad-field ad-field--sm">
              <label>Order</label>
              <input className="ad-input" type="number" min={1} value={phase.order}
                onChange={(e) => setField("order", Number(e.target.value))} />
            </div>
            <div className="ad-field ad-field--sm">
              <label>Readiness %</label>
              <input className="ad-input" type="number" min={0} max={100} value={phase.readinessWeight}
                onChange={(e) => setField("readinessWeight", Number(e.target.value))} />
            </div>
          </div>

          <div className="ad-field">
            <label>Description</label>
            <textarea className="ad-textarea" rows={2} value={phase.description || ""}
              onChange={(e) => setField("description", e.target.value)}
              placeholder="What will learners achieve in this phase?" />
          </div>

          <div className="ad-field-row">
            <div className="ad-field">
              <label>Assessment Difficulty</label>
              <select className="ad-select" value={phase.assessmentDifficulty}
                onChange={(e) => setField("assessmentDifficulty", e.target.value)}>
                <option value="normal">Normal</option>
                <option value="hard">Hard</option>
              </select>
            </div>
            <div className="ad-field" style={{ justifyContent: "flex-end" }}>
              <label>
                <input type="checkbox" checked={phase.unlocksAfterPrevious}
                  onChange={(e) => setField("unlocksAfterPrevious", e.target.checked)} />
                {" "}Unlocks after previous
              </label>
            </div>
          </div>

          <div className="ad-field">
            <label>Assessment Video IDs (YouTube IDs — comma/space/newline separated)</label>
            <textarea className="ad-textarea ad-textarea--mono" rows={2}
              value={(phase.assessmentVideoIds || []).join(", ")}
              onChange={(e) => setAssessmentIds(e.target.value)}
              placeholder="e.g. bMknfKXIFA8, pQN-pnXPaVg" />
            {phaseQuizStatus && (
              <div className="ad-quiz-detail">
                {phaseQuizStatus.quizzes?.map((q) => (
                  <span key={q.videoId} className={`ad-quiz-vid ${q.exists ? "ad-quiz-vid--ok" : "ad-quiz-vid--missing"}`}>
                    {q.videoId} {q.exists ? "✓" : "○"}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="ad-section-label">
            📦 Resources
            <button className="ad-add-btn" onClick={addResource}>+ Add Resource</button>
          </div>
          <div className="ad-resource-list">
            {phase.resources?.map((res, i) => (
              <ResourceRow key={res._uid || i} res={res}
                onChange={(val) => setResource(i, val)}
                onDelete={() => deleteResource(i)} />
            ))}
          </div>

          <div className="ad-section-label">🚀 Phase Project</div>
          <div className="ad-project-block">
            <div className="ad-field-row">
              <div className="ad-field ad-field--grow">
                <label>Project Title</label>
                <input className="ad-input" value={phase.project?.title || ""}
                  onChange={(e) => setProject("title", e.target.value)} />
              </div>
              <div className="ad-field ad-field--grow">
                <label>Example URL (optional)</label>
                <input className="ad-input" value={phase.project?.exampleUrl || ""}
                  onChange={(e) => setProject("exampleUrl", e.target.value)} placeholder="https://..." />
              </div>
            </div>
            <div className="ad-field">
              <label>Description</label>
              <textarea className="ad-textarea" rows={3} value={phase.project?.description || ""}
                onChange={(e) => setProject("description", e.target.value)} />
            </div>
            <div className="ad-field">
              <label>Required Skills (comma separated)</label>
              <input className="ad-input"
                value={(phase.project?.requiredSkills || []).join(", ")}
                onChange={(e) =>
                  setProject("requiredSkills", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))
                }
                placeholder="React, Hooks, REST API" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   § TrackEditor — visual editor with quiz + course controls
   ============================================================ */
function TrackEditor({ initial, tracks, onSave, onCancel, saving }) {
  const [track, setTrack] = useState(
    initial
      ? { ...initial, phases: initial.phases?.map((p) => ({ ...p, _uid: uid() })) || [] }
      : EMPTY_TRACK()
  );
  const [activeTab, setActiveTab] = useState("overview");
  const [tagInput, setTagInput] = useState((initial?.tags || []).join(", "));
  const [quizStatus, setQuizStatus] = useState(null);
  const [generatingQuizPhase, setGeneratingQuizPhase] = useState(null);
  const [linkingCourses, setLinkingCourses] = useState(false);
  const [generatingAllQuizzes, setGeneratingAllQuizzes] = useState(false);
  const [actionLog, setActionLog] = useState([]);

  const appendLog = (msg) => setActionLog((l) => [...l, { ts: Date.now(), msg }]);

  // Load quiz status for existing tracks
  useEffect(() => {
    if (initial?.slug) {
      ax("get", `/admin/tracks/${initial.slug}/quiz-status`)
        .then((r) => setQuizStatus(r.data))
        .catch(() => { });
    }
  }, [initial?.slug]);

  const set = (key, val) => setTrack((t) => ({ ...t, [key]: val }));
  const autoSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const addPhase = () => {
    const order = (track.phases?.length || 0) + 1;
    setTrack((t) => ({ ...t, phases: [...(t.phases || []), EMPTY_PHASE(order)] }));
  };

  const updatePhase = (i, val) => {
    const phases = [...track.phases]; phases[i] = val;
    setTrack((t) => ({ ...t, phases }));
  };

  const deletePhase = (i) => {
    if (!window.confirm("Delete this phase?")) return;
    const phases = track.phases.filter((_, j) => j !== i).map((p, j) => ({ ...p, order: j + 1 }));
    setTrack((t) => ({ ...t, phases }));
  };

  const movePhase = (i, dir) => {
    const phases = [...track.phases];
    const j = i + dir;
    if (j < 0 || j >= phases.length) return;
    [phases[i], phases[j]] = [phases[j], phases[i]];
    phases.forEach((p, k) => { p.order = k + 1; });
    setTrack((t) => ({ ...t, phases }));
  };

  const handleSave = () => {
    const payload = {
      ...track,
      tags: tagInput.split(",").map((s) => s.trim()).filter(Boolean),
      phases: track.phases.map(({ _uid, ...p }) => ({
        ...p,
        resources: p.resources?.map(({ _uid: _r, ...r }) => r) || [],
      })),
    };
    onSave(payload);
  };

  // Generate quizzes for a single phase
  const handleGeneratePhaseQuiz = async (phaseOrder) => {
    if (!initial?.slug) return alert("Save the track first before generating quizzes.");
    setGeneratingQuizPhase(phaseOrder);
    appendLog(`🤖 Generating quizzes for Phase ${phaseOrder}...`);
    try {
      const res = await ax("post", `/admin/tracks/${initial.slug}/generate-quizzes`, { phaseOrder });
      appendLog(`✅ Done: ${JSON.stringify(res.data.results)}`);
      // Refresh quiz status
      const statusRes = await ax("get", `/admin/tracks/${initial.slug}/quiz-status`);
      setQuizStatus(statusRes.data);
    } catch (err) {
      appendLog(`❌ Failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setGeneratingQuizPhase(null);
    }
  };

  // Generate all quizzes
  const handleGenerateAllQuizzes = async () => {
    if (!initial?.slug) return alert("Save the track first before generating quizzes.");
    setGeneratingAllQuizzes(true);
    appendLog("🤖 Generating ALL phase quizzes...");
    try {
      const res = await ax("post", `/admin/tracks/${initial.slug}/generate-quizzes`, {});
      appendLog(`✅ All quizzes done: ${JSON.stringify(res.data.results)}`);
      const statusRes = await ax("get", `/admin/tracks/${initial.slug}/quiz-status`);
      setQuizStatus(statusRes.data);
    } catch (err) {
      appendLog(`❌ Failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setGeneratingAllQuizzes(false);
    }
  };

  // Link / re-link playlist courses
  const handleLinkCourses = async () => {
    if (!initial?.slug) return alert("Save the track first.");
    setLinkingCourses(true);
    appendLog("📦 Linking playlist resources to course documents...");
    try {
      const res = await ax("post", `/admin/tracks/${initial.slug}/link-courses`, {});
      const linked = res.data.linkLog?.filter((l) => l.status === "linked").length || 0;
      const failed = res.data.linkLog?.filter((l) => l.status !== "linked").length || 0;
      appendLog(`✅ Linked: ${linked}, Failed: ${failed}`);
    } catch (err) {
      appendLog(`❌ Failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setLinkingCourses(false);
    }
  };

  const totalWeight = (track.phases || []).reduce((s, p) => s + (p.readinessWeight || 0), 0);
  const phasesWithVideos = (track.phases || []).filter((p) => p.assessmentVideoIds?.length > 0).length;

  return (
    <div className="ad-editor">
      <div className="ad-editor-topbar">
        <div>
          <h2 className="ad-editor-title">{initial ? "✏️ Edit Track" : "✨ New Track"}</h2>
          <p className="ad-editor-slug">/{track.slug || "slug-will-appear-here"}</p>
        </div>
        <div className="ad-editor-topbar-actions">
          {initial && (
            <>
              <button className="ad-btn ad-btn--ghost ad-btn--sm" onClick={handleLinkCourses} disabled={linkingCourses}>
                {linkingCourses ? "⏳" : "📦"} Link Courses
              </button>
              <button className="ad-btn ad-btn--ghost ad-btn--sm" onClick={handleGenerateAllQuizzes} disabled={generatingAllQuizzes}>
                {generatingAllQuizzes ? "⏳" : "🤖"} Gen All Quizzes
              </button>
            </>
          )}
          <button className="ad-btn ad-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="ad-btn ad-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : initial ? "💾 Save Changes" : "🚀 Create Track"}
          </button>
        </div>
      </div>

      {/* Action log */}
      {actionLog.length > 0 && (
        <div className="ad-action-log">
          {actionLog.slice(-5).map((l) => (
            <div key={l.ts} className="ad-action-log-entry">{l.msg}</div>
          ))}
        </div>
      )}

      <div className="ad-editor-tabs">
        {TABS.map((t) => (
          <button key={t} className={`ad-tab ${activeTab === t ? "ad-tab--active" : ""}`}
            onClick={() => setActiveTab(t)}>
            {t === "overview" && "📋 Overview"}
            {t === "phases" && `📚 Phases (${track.phases?.length || 0})`}
            {t === "settings" && "⚙️ Settings"}
          </button>
        ))}
      </div>

      <div className="ad-editor-body">
        {/* ── OVERVIEW TAB ── */}
        {activeTab === "overview" && (
          <div className="ad-tab-content">
            <div className="ad-field-row">
              <div className="ad-field ad-field--grow">
                <label>Track Title *</label>
                <input className="ad-input" value={track.title}
                  onChange={(e) => {
                    set("title", e.target.value);
                    if (!initial) set("slug", autoSlug(e.target.value));
                  }} placeholder="e.g. Frontend Internship Track" />
              </div>
              <div className="ad-field ad-field--grow">
                <label>Slug *</label>
                <input className="ad-input ad-input--mono" value={track.slug}
                  onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/\s/g, "-"))}
                  placeholder="frontend-internship" />
              </div>
            </div>

            <div className="ad-field">
              <label>Description</label>
              <textarea className="ad-textarea" rows={3} value={track.description || ""}
                onChange={(e) => set("description", e.target.value)}
                placeholder="What will learners achieve? Who is this for?" />
            </div>

            <div className="ad-field-row">
              <div className="ad-field">
                <label>Audience</label>
                <select className="ad-select" value={track.audience} onChange={(e) => set("audience", e.target.value)}>
                  {AUDIENCES.map((a) => <option key={a}>{a}</option>)}
                </select>
              </div>
              <div className="ad-field">
                <label>Goal</label>
                <select className="ad-select" value={track.goal} onChange={(e) => set("goal", e.target.value)}>
                  {GOALS.map((g) => <option key={g}>{g}</option>)}
                </select>
              </div>
              <div className="ad-field">
                <label>Category</label>
                <select className="ad-select" value={track.category} onChange={(e) => set("category", e.target.value)}>
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="ad-field ad-field--sm">
                <label>Est. Months</label>
                <input className="ad-input" type="number" min={1} value={track.estimatedMonths}
                  onChange={(e) => set("estimatedMonths", Number(e.target.value))} />
              </div>
            </div>

            <div className="ad-field">
              <label>Thumbnail URL (optional)</label>
              <input className="ad-input" value={track.thumbnail || ""}
                onChange={(e) => set("thumbnail", e.target.value)} placeholder="https://..." />
            </div>

            <div className="ad-field">
              <label>Tags (comma separated)</label>
              <input className="ad-input" value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="react, javascript, internship" />
            </div>

            <div className="ad-field">
              <label>Prerequisites (select tracks that should be completed before this one)</label>
              <div className="ad-prereq-selector">
                {(tracks || []).filter(t => t.slug !== track.slug).map(t => {
                  // track.prerequisites might be array of IDs or array of populated objects
                  const isSelected = track.prerequisites?.some(p => (p._id || p) === t._id);
                  return (
                    <label key={t._id} className={`ad-prereq-chip ${isSelected ? "active" : ""}`}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          const current = track.prerequisites || [];
                          if (e.target.checked) {
                            set("prerequisites", [...current, t._id]);
                          } else {
                            set("prerequisites", current.filter(id => (id._id || id) !== t._id));
                          }
                        }}
                      />
                      {t.title}
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── PHASES TAB ── */}
        {activeTab === "phases" && (
          <div className="ad-tab-content">
            <div className="ad-phases-topbar">
              <div className="ad-phases-topbar-left">
                <div className="ad-weight-badge">
                  <span className={`ad-weight-val ${totalWeight === 100 ? "ad-weight-val--ok" : totalWeight > 100 ? "ad-weight-val--over" : "ad-weight-val--under"}`}>
                    {totalWeight}%
                  </span>
                  <span className="ad-weight-label">
                    readiness weight {totalWeight !== 100 && "(should be 100)"}
                  </span>
                </div>
                {phasesWithVideos > 0 && (
                  <span className="ad-quiz-coverage-pill">
                    🎯 {phasesWithVideos} phase{phasesWithVideos > 1 ? "s" : ""} with assessments
                  </span>
                )}
              </div>
              <button className="ad-btn ad-btn--accent" onClick={addPhase}>+ Add Phase</button>
            </div>

            {(track.phases || []).map((phase, i) => (
              <PhaseCard
                key={phase._uid || phase._id || i}
                phase={phase} idx={i} total={track.phases.length}
                onChange={(val) => updatePhase(i, val)}
                onDelete={() => deletePhase(i)}
                onMove={movePhase}
                quizStatus={quizStatus}
                onGenerateQuiz={handleGeneratePhaseQuiz}
                generatingQuiz={generatingQuizPhase === phase.order}
              />
            ))}

            {track.phases?.length === 0 && (
              <div className="ad-empty">No phases yet. Click "+ Add Phase" to get started.</div>
            )}
          </div>
        )}

        {/* ── SETTINGS TAB ── */}
        {activeTab === "settings" && (
          <div className="ad-tab-content">
            <div className="ad-settings-grid">
              <div className="ad-setting-card">
                <div className="ad-setting-label">Published</div>
                <label className="ad-toggle">
                  <input type="checkbox" checked={track.isPublished}
                    onChange={(e) => set("isPublished", e.target.checked)} />
                  <span className="ad-toggle-slider" />
                </label>
                <p className="ad-setting-desc">When ON, this track is visible to all users.</p>
              </div>

              <div className="ad-setting-card">
                <div className="ad-setting-label">Pro Track</div>
                <label className="ad-toggle">
                  <input type="checkbox" checked={track.isPro}
                    onChange={(e) => set("isPro", e.target.checked)} />
                  <span className="ad-toggle-slider" />
                </label>
                <p className="ad-setting-desc">Entire track requires a Pro subscription.</p>
              </div>

              <div className="ad-setting-card">
                <div className="ad-setting-label">Free Phase Count</div>
                <input className="ad-input" type="number" min={0}
                  max={track.phases?.length || 10} value={track.freePhaseCount}
                  onChange={(e) => set("freePhaseCount", Number(e.target.value))}
                  disabled={track.isPro} />
                <p className="ad-setting-desc">Phases free users can access (ignored if Pro Track is ON).</p>
              </div>
            </div>

            {/* Quiz generation panel for existing tracks */}
            {initial && (
              <div className="ad-quiz-panel">
                <div className="ad-quiz-panel-header">
                  <span>🤖 Quiz Generation Status</span>
                  <button className="ad-btn ad-btn--ghost ad-btn--sm"
                    onClick={() => ax("get", `/admin/tracks/${initial.slug}/quiz-status`).then((r) => setQuizStatus(r.data))}>
                    ↻ Refresh
                  </button>
                </div>
                {quizStatus ? (
                  <div className="ad-quiz-phase-list">
                    {quizStatus.phases?.map((ps) => (
                      <div key={ps.phaseId} className="ad-quiz-phase-row">
                        <div className="ad-quiz-phase-info">
                          <span className="ad-quiz-phase-name">P{ps.order} {ps.title}</span>
                          <span className={`ad-quiz-badge ${ps.allGenerated ? "ad-quiz-badge--done" : ps.videoCount > 0 ? "ad-quiz-badge--partial" : "ad-quiz-badge--none"}`}>
                            {ps.videoCount === 0 ? "No Assessment" : ps.allGenerated ? "✓ Ready" : `${ps.quizzes?.filter((q) => q.exists).length || 0}/${ps.videoCount}`}
                          </span>
                          <span className="ad-quiz-diff-badge">{ps.assessmentDifficulty}</span>
                        </div>
                        {ps.videoCount > 0 && !ps.allGenerated && (
                          <button className="ad-btn ad-btn--accent ad-btn--sm"
                            onClick={() => handleGeneratePhaseQuiz(ps.order)}
                            disabled={generatingQuizPhase === ps.order}>
                            {generatingQuizPhase === ps.order ? "⏳ Generating…" : "🤖 Generate"}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="ad-quiz-panel-empty">Loading quiz status…</p>
                )}
              </div>
            )}

            <div className="ad-json-preview">
              <div className="ad-section-label">📋 JSON Preview (read-only)</div>
              <pre className="ad-json-pre">
                {JSON.stringify({
                  ...track,
                  tags: tagInput.split(",").map((s) => s.trim()).filter(Boolean),
                  phases: track.phases?.map(({ _uid, ...p }) => ({
                    ...p,
                    resources: p.resources?.map(({ _uid: _r, ...r }) => r),
                  })),
                }, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   § DuplicateModal
   ============================================================ */
function DuplicateModal({ track, onConfirm, onClose }) {
  const [newSlug, setNewSlug] = useState(`${track.slug}-copy`);
  return (
    <div className="ad-modal-overlay" onClick={onClose}>
      <div className="ad-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="ad-modal-title">Duplicate Track</h3>
        <p className="ad-modal-sub">Duplicating: <strong>{track.title}</strong></p>
        <div className="ad-field" style={{ marginTop: 12 }}>
          <label>New Slug *</label>
          <input className="ad-input ad-input--mono" value={newSlug}
            onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/\s/g, "-"))}
            placeholder="my-track-copy" />
        </div>
        <div className="ad-modal-actions">
          <button className="ad-btn ad-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="ad-btn ad-btn--primary" onClick={() => onConfirm(newSlug)} disabled={!newSlug}>
            🔁 Duplicate
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   § Analytics Panel
   ============================================================ */
function AnalyticsPanel({ analytics }) {
  if (!analytics) return <div className="ad-empty">Loading analytics…</div>;
  //removed goalBreakdown
  const { summary, topTracks, categoryBreakdown, quizCoverage, quizByDifficulty, recentContributions } = analytics;

  const quizCoveragePercent = quizCoverage.phasesWithAssessments > 0
    ? Math.round((quizCoverage.phasesGenerated / quizCoverage.phasesWithAssessments) * 100)
    : 0;

  return (
    <div className="ad-analytics">
      {/* ── Summary grid ── */}
      <div className="ad-analytics-grid">
        {[
          { icon: "👤", label: "Total Users", val: summary.totalUsers, sub: `+${summary.newUsersLast7Days} this week` },
          { icon: "📚", label: "Published Tracks", val: summary.totalPublished, sub: `${summary.draftTracks} drafts` },
          { icon: "🎬", label: "Courses Indexed", val: summary.totalCourses, sub: "YouTube playlists" },
          { icon: "🧠", label: "Quizzes Cached", val: summary.totalQuizzes, sub: `${quizCoveragePercent}% phase coverage` },
          { icon: "📐", label: "Total Phases", val: summary.totalPhases, sub: `avg ${summary.avgPhasesPerTrack} per track` },
          { icon: "💬", label: "Contributions", val: summary.totalContributions, sub: `${summary.pendingContributions} pending`, alert: summary.pendingContributions > 0 },
        ].map((s) => (
          <div key={s.label} className={`ad-analytics-card ${s.alert ? "ad-analytics-card--alert" : ""}`}>
            <div className="ad-analytics-card-icon">{s.icon}</div>
            <div className="ad-analytics-card-val">{s.val?.toLocaleString()}</div>
            <div className="ad-analytics-card-label">{s.label}</div>
            {s.sub && <div className="ad-analytics-card-sub">{s.sub}</div>}
          </div>
        ))}
      </div>
      {/* ── Growth & Viral Performance ── */}
      {summary.growth && (
        <div className="ad-analytics-section" style={{ marginTop: 16 }}>
          <div className="ad-analytics-section-title">📊 Growth & Viral Performance</div>
          <div className="ad-growth-grid">
            <div className="ad-growth-card">
              <span className="ad-growth-label">Referral Conv. Rate</span>
              <span className="ad-growth-val">{summary.growth.referralConvRate}%</span>
              <span className="ad-growth-sub">{summary.growth.referralSuccess} / {summary.growth.totalReferrals} successful</span>
            </div>
            <div className="ad-growth-card">
              <span className="ad-growth-label">Phase 1 Completions</span>
              <span className="ad-growth-val">{summary.growth.phase1Completions}</span>
              <span className="ad-growth-sub">Learners passed first gate</span>
            </div>
            <div className="ad-growth-card">
              <span className="ad-growth-label">PRO Active Subs</span>
              <span className="ad-growth-val">{summary.growth.proTotal}</span>
              <span className="ad-growth-sub">Paid conversion floor</span>
            </div>
            <div className="ad-growth-card">
              <span className="ad-growth-label">Active User Rate</span>
              <span className="ad-growth-val">
                {summary.totalUsers > 0 ? Math.round((summary.growth.active7d / summary.totalUsers) * 100) : 0}%
              </span>
              <span className="ad-growth-sub">{summary.growth.active7d} users active this week</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Two-column: top tracks + breakdowns ── */}
      <div className="ad-analytics-row">
        {/* Top tracks */}
        <div className="ad-analytics-section">
          <div className="ad-analytics-section-title">🏆 Top Tracks by Enrollment</div>
          {topTracks?.map((t, i) => (
            <div key={t.slug} className="ad-analytics-track-row">
              <span className="ad-analytics-rank">#{i + 1}</span>
              <div className="ad-analytics-track-info">
                <span className="ad-analytics-track-name">{t.title}</span>
                <div className="ad-analytics-track-meta">
                  <span className="ad-pill ad-pill--cat">{t.category}</span>
                  <span className="ad-pill-neutral">{t.phaseCount} phases</span>
                  {!t.isPublished && <span className="ad-pill ad-pill--draft">draft</span>}
                </div>
              </div>
              <span className="ad-analytics-enroll">👥 {t.enrollmentCount}</span>
            </div>
          ))}
        </div>

        {/* Breakdowns */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Category breakdown */}
          <div className="ad-analytics-section">
            <div className="ad-analytics-section-title">📊 By Category</div>
            {categoryBreakdown?.map((c) => (
              <div key={c._id} className="ad-breakdown-row">
                <span className="ad-breakdown-label">{c._id}</span>
                <div className="ad-breakdown-bar-wrap">
                  <div className="ad-breakdown-bar"
                    style={{ width: `${Math.min(100, (c.count / (categoryBreakdown[0]?.count || 1)) * 100)}%` }} />
                </div>
                <span className="ad-breakdown-val">{c.count}</span>
              </div>
            ))}
          </div>

          {/* Quiz generation status */}
          <div className="ad-analytics-section">
            <div className="ad-analytics-section-title">🤖 Quiz Coverage</div>
            <div className="ad-quiz-cov-row">
              <span className="ad-quiz-cov-label">Phases with assessments</span>
              <span className="ad-quiz-cov-val">{quizCoverage.phasesWithAssessments}</span>
            </div>
            <div className="ad-quiz-cov-row">
              <span className="ad-quiz-cov-label">Quizzes generated</span>
              <span className="ad-quiz-cov-val" style={{ color: "var(--ad-green)" }}>{quizCoverage.phasesGenerated}</span>
            </div>
            <div className="ad-quiz-cov-row">
              <span className="ad-quiz-cov-label">Coverage</span>
              <span className={`ad-quiz-cov-val ${quizCoveragePercent === 100 ? "ad-quiz-cov--ok" : quizCoveragePercent > 50 ? "ad-quiz-cov--partial" : "ad-quiz-cov--low"}`}>
                {quizCoveragePercent}%
              </span>
            </div>
            <div className="ad-cov-bar-wrap">
              <div className="ad-cov-bar" style={{ width: `${quizCoveragePercent}%` }} />
            </div>
            {quizByDifficulty?.map((q) => (
              <div key={`${q._id.difficulty}-${q._id.source}`} className="ad-quiz-cov-row" style={{ marginTop: 4 }}>
                <span className="ad-quiz-cov-label" style={{ fontSize: 11 }}>
                  {q._id.difficulty} / {q._id.source}
                </span>
                <span className="ad-quiz-cov-val" style={{ fontSize: 12 }}>{q.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent contributions */}
      {recentContributions?.length > 0 && (
        <div className="ad-analytics-section" style={{ marginTop: 0 }}>
          <div className="ad-analytics-section-title">💬 Recent Contributions (30d)</div>
          {recentContributions.map((c) => (
            <div key={c._id} className="ad-recent-con-row">
              <span className={`ad-pill ad-pill--status-${c.status}`}>{c.status}</span>
              <span className="ad-recent-con-title">{c.resourceData?.title || "—"}</span>
              <span className="ad-recent-con-meta">by {c.user?.name || "?"} · {c.track?.title || "?"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   § User Management Helpers & Components
   ============================================================ */

const fmtRelativeTime = (dateStr) => {
  if (!dateStr) return "Never";
  const date = new Date(dateStr);
  const now = new Date();
  const diffSec = Math.floor((now - date) / 1000);
  if (diffSec < 60) return "Just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}h ago`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  const diffMonth = Math.floor(diffDay / 30);
  if (diffMonth < 12) return `${diffMonth}mo ago`;
  return `${Math.floor(diffMonth / 12)}y ago`;
};

const fmtDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return "—";
  }
};

const getAvatarColor = (str = "") => {
  const colors = ["#3b82f6", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#06b6d4", "#6366f1"];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

/* ── User Details Modal ── */
function UserDetailsModal({ userId, details, loading, onClose, onCopy }) {
  const [activeTab, setActiveTab] = useState("overview");

  if (!userId) return null;

  const user = details?.user;
  const enrollments = details?.enrollments || [];
  const contributions = details?.contributions || [];
  const referrals = details?.referrals || [];

  return (
    <div className="ad-modal-overlay" onClick={onClose}>
      <div className="ad-modal ad-modal--lg" onClick={(e) => e.stopPropagation()}>
        <div className="ad-modal-header">
          <div className="ad-user-modal-title">
            <div
              className="ad-user-avatar-lg"
              style={{ backgroundColor: getAvatarColor(user?.name || user?.email) }}
            >
              {user?.avatar ? (
                <img src={user.avatar} alt={user?.name} />
              ) : (
                (user?.name?.[0] || user?.email?.[0] || "U").toUpperCase()
              )}
            </div>
            <div>
              <div className="ad-user-modal-name-row">
                <h3 className="ad-modal-title">{user?.name || "User Details"}</h3>
                {user?.role === "admin" && <span className="ad-user-badge ad-user-badge--admin">🛡️ Admin</span>}
                {user?.isFoundingUser && <span className="ad-user-badge ad-user-badge--founding">🏅 Founding</span>}
                {user?.isVerified ? (
                  <span className="ad-user-badge ad-user-badge--verified">✓ Verified</span>
                ) : (
                  <span className="ad-user-badge ad-user-badge--unverified">Unverified</span>
                )}
                <span
                  className={`ad-user-badge ${
                    user?.subscription?.plan === "PRO" ? "ad-user-badge--pro" : "ad-user-badge--free"
                  }`}
                >
                  {user?.subscription?.plan === "PRO" ? "⭐ PRO" : "FREE"}
                </span>
              </div>
              <div className="ad-user-modal-sub-row">
                <span className="ad-user-modal-email">{user?.email}</span>
                <button
                  className="ad-icon-btn-tiny"
                  onClick={() => onCopy(user?.email, "Email")}
                  title="Copy email"
                >
                  📋
                </button>
                <span className="ad-user-modal-id">ID: {user?._id}</span>
                <button
                  className="ad-icon-btn-tiny"
                  onClick={() => onCopy(user?._id, "User ID")}
                  title="Copy ID"
                >
                  📋
                </button>
              </div>
            </div>
          </div>
          <button className="ad-icon-btn ad-icon-btn--close" onClick={onClose} title="Close">
            ✕
          </button>
        </div>

        {loading ? (
          <div className="ad-modal-loading">
            <div className="ad-spinner" />
            <p>Fetching full user details…</p>
          </div>
        ) : !user ? (
          <div className="ad-empty">User data not found or failed to load.</div>
        ) : (
          <>
            <div className="ad-modal-tabs">
              {[
                { key: "overview", label: "👤 Profile & Activity" },
                { key: "subscription", label: "💳 Subscription & Billing" },
                { key: "gamification", label: "🎮 Gamification" },
                { key: "onboarding", label: "🎯 Onboarding Profile" },
                { key: "tracks", label: `📚 Enrolled Tracks (${enrollments.length})` },
                { key: "activity", label: `💬 Referrals & Contributions (${referrals.length + contributions.length})` },
              ].map((t) => (
                <button
                  key={t.key}
                  className={`ad-modal-tab-btn ${activeTab === t.key ? "ad-modal-tab-btn--active" : ""}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="ad-modal-body">
              {/* ── OVERVIEW TAB ── */}
              {activeTab === "overview" && (
                <div className="ad-grid-2col">
                  <div className="ad-info-box">
                    <div className="ad-info-box-title">Account Identity</div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Full Name:</span>
                      <span className="ad-kv-val">{user.name}</span>
                    </div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Email:</span>
                      <span className="ad-kv-val">{user.email}</span>
                    </div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Auth Method:</span>
                      <span className="ad-kv-val">{user.googleId ? "Google OAuth 🌐" : "Email / Password 🔐"}</span>
                    </div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Role:</span>
                      <span className="ad-kv-val">
                        <strong style={{ textTransform: "uppercase" }}>{user.role}</strong>
                      </span>
                    </div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Referral Code:</span>
                      <span className="ad-kv-val">
                        <code>{user.referralCode || "None"}</code>
                      </span>
                    </div>
                  </div>

                  <div className="ad-info-box">
                    <div className="ad-info-box-title">Session & Timeline</div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Account Created:</span>
                      <span className="ad-kv-val">{fmtDate(user.createdAt)} ({fmtRelativeTime(user.createdAt)})</span>
                    </div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Last Active:</span>
                      <span className="ad-kv-val">{fmtDate(user.lastActiveAt)} ({fmtRelativeTime(user.lastActiveAt)})</span>
                    </div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Timezone:</span>
                      <span className="ad-kv-val">{user.timezone || "Asia/Kolkata"}</span>
                    </div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Email Verified:</span>
                      <span className="ad-kv-val">{user.isVerified ? "✅ Yes" : "❌ No"}</span>
                    </div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Founding Member:</span>
                      <span className="ad-kv-val">{user.isFoundingUser ? "🏅 Yes" : "No"}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* ── SUBSCRIPTION TAB ── */}
              {activeTab === "subscription" && (
                <div className="ad-info-box">
                  <div className="ad-info-box-title">Subscription & Payment Status</div>
                  <div className="ad-grid-2col" style={{ marginTop: 12 }}>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Plan Tier:</span>
                      <span className="ad-kv-val">
                        <span className={`ad-pill ${user.subscription?.plan === "PRO" ? "ad-pill--pro" : "ad-pill-neutral"}`}>
                          {user.subscription?.plan || "FREE"}
                        </span>
                      </span>
                    </div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Subscription Status:</span>
                      <span className="ad-kv-val">
                        <span className={`ad-user-sub-status ad-user-sub-status--${(user.subscription?.status || "INACTIVE").toLowerCase()}`}>
                          ● {user.subscription?.status || "INACTIVE"}
                        </span>
                      </span>
                    </div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Payment Provider:</span>
                      <span className="ad-kv-val">
                        <code>{user.subscription?.provider || "None"}</code>
                      </span>
                    </div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Subscription ID:</span>
                      <span className="ad-kv-val">
                        <code>{user.subscription?.subscriptionId || "N/A"}</code>
                      </span>
                    </div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Current Period End:</span>
                      <span className="ad-kv-val">
                        {user.subscription?.currentPeriodEnd ? fmtDate(user.subscription.currentPeriodEnd) : "N/A"}
                      </span>
                    </div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Is Active Paid:</span>
                      <span className="ad-kv-val">
                        {user.subscription?.plan === "PRO" && user.subscription?.status === "ACTIVE" ? "✅ Active Pro" : "❌ Free / Inactive"}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* ── GAMIFICATION TAB ── */}
              {activeTab === "gamification" && (
                <div className="ad-grid-3col">
                  <div className="ad-metric-card">
                    <span className="ad-metric-icon">⭐</span>
                    <span className="ad-metric-val">{user.level || 1}</span>
                    <span className="ad-metric-label">Current Level</span>
                  </div>
                  <div className="ad-metric-card">
                    <span className="ad-metric-icon">⚡</span>
                    <span className="ad-metric-val">{(user.xp || 0).toLocaleString()}</span>
                    <span className="ad-metric-label">Total XP</span>
                  </div>
                  <div className="ad-metric-card">
                    <span className="ad-metric-icon">🪙</span>
                    <span className="ad-metric-val">{(user.credits || 0).toLocaleString()}</span>
                    <span className="ad-metric-label">Credits Balance</span>
                  </div>
                  <div className="ad-metric-card">
                    <span className="ad-metric-icon">🎬</span>
                    <span className="ad-metric-val">{user.courseCount || 0}</span>
                    <span className="ad-metric-label">Courses Added</span>
                  </div>
                  <div className="ad-metric-card">
                    <span className="ad-metric-icon">🗺️</span>
                    <span className="ad-metric-val">{user.roadmapCount || 0}</span>
                    <span className="ad-metric-label">Roadmaps Created</span>
                  </div>
                  <div className="ad-metric-card">
                    <span className="ad-metric-icon">🎯</span>
                    <span className="ad-metric-val">{enrollments.length}</span>
                    <span className="ad-metric-label">Enrolled Tracks</span>
                  </div>
                </div>
              )}

              {/* ── ONBOARDING TAB ── */}
              {activeTab === "onboarding" && (
                <div className="ad-info-box">
                  <div className="ad-info-box-title">
                    Onboarding Questionnaire {user.onboarding?.completed ? "✅ (Completed)" : "⏳ (Not Completed)"}
                  </div>
                  <div className="ad-grid-2col" style={{ marginTop: 12 }}>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Primary Goal:</span>
                      <span className="ad-kv-val">{user.onboarding?.primaryGoal || "—"}</span>
                    </div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Target Role:</span>
                      <span className="ad-kv-val">{user.onboarding?.targetRole || "—"}</span>
                    </div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Focus Area:</span>
                      <span className="ad-kv-val">{user.onboarding?.focusArea || "—"}</span>
                    </div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Experience Level:</span>
                      <span className="ad-kv-val">{user.onboarding?.experienceLevel || "—"}</span>
                    </div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Daily Commitment:</span>
                      <span className="ad-kv-val">{user.onboarding?.dailyCommitment || "—"}</span>
                    </div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Timeline:</span>
                      <span className="ad-kv-val">{user.onboarding?.timeline || "—"}</span>
                    </div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Target Month:</span>
                      <span className="ad-kv-val">{user.onboarding?.targetMonth || "—"}</span>
                    </div>
                    <div className="ad-kv-row">
                      <span className="ad-kv-key">Study Time Target:</span>
                      <span className="ad-kv-val">{user.onboarding?.studyTimeTarget || "—"}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* ── TRACKS TAB ── */}
              {activeTab === "tracks" && (
                <div className="ad-track-enrollment-list">
                  {enrollments.length === 0 ? (
                    <div className="ad-empty">User is not enrolled in any tracks yet.</div>
                  ) : (
                    enrollments.map((en) => (
                      <div key={en._id} className="ad-enrollment-row">
                        <div className="ad-enrollment-thumb">
                          {en.track?.thumbnail ? (
                            <img src={en.track.thumbnail} alt="" />
                          ) : (
                            <div className="ad-track-thumb-placeholder">{en.track?.category?.[0]?.toUpperCase() || "T"}</div>
                          )}
                        </div>
                        <div className="ad-enrollment-info">
                          <div className="ad-enrollment-title">
                            {en.track?.title || "Unknown Track"}
                            {en.track?.isPro && <span className="ad-pill ad-pill--pro" style={{ marginLeft: 8 }}>PRO</span>}
                            {en.completed && <span className="ad-pill ad-pill--quiz-ok" style={{ marginLeft: 8 }}>✓ Finished</span>}
                          </div>
                          <div className="ad-enrollment-meta">
                            <span>Phase {en.currentPhase || 1} of {en.track?.phases?.length || "?"}</span>
                            <span>·</span>
                            <span>Readiness Score: <strong>{en.readinessScore || 0}%</strong></span>
                            <span>·</span>
                            <span>Enrolled: {fmtDate(en.createdAt)}</span>
                          </div>
                          <div className="ad-progress-bar-wrap" style={{ marginTop: 6 }}>
                            <div className="ad-progress-bar-fill" style={{ width: `${en.readinessScore || 0}%` }} />
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* ── ACTIVITY & REFERRALS TAB ── */}
              {activeTab === "activity" && (
                <div className="ad-grid-2col">
                  <div className="ad-info-box">
                    <div className="ad-info-box-title">Referrals ({referrals.length})</div>
                    {referrals.length === 0 ? (
                      <p className="ad-kv-val" style={{ color: "var(--ad-text-muted)" }}>No referral history.</p>
                    ) : (
                      <div className="ad-mini-list">
                        {referrals.map((r) => (
                          <div key={r._id} className="ad-mini-list-item">
                            <span>{(r.referee?.name || r.referredUser?.name || "Referred user")} ({(r.referee?.email || r.referredUser?.email || "")})</span>
                            <span className="ad-pill ad-pill-neutral">{fmtDate(r.createdAt)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="ad-info-box">
                    <div className="ad-info-box-title">Community Contributions ({contributions.length})</div>
                    {contributions.length === 0 ? (
                      <p className="ad-kv-val" style={{ color: "var(--ad-text-muted)" }}>No contributions submitted.</p>
                    ) : (
                      <div className="ad-mini-list">
                        {contributions.map((c) => (
                          <div key={c._id} className="ad-mini-list-item">
                            <div>
                              <strong>{c.resourceData?.title || "Resource"}</strong>
                              <div style={{ fontSize: 11, color: "var(--ad-text-muted)" }}>{c.track?.title || "Track"}</div>
                            </div>
                            <span className={`ad-pill ad-pill--status-${c.status}`}>{c.status}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   § Users Management Panel
   ============================================================ */
function UsersPanel({
  users,
  stats,
  loading,
  filters,
  pagination,
  onFilterChange,
  onResetFilters,
  onPageChange,
  onInspectUser,
  onCopy,
}) {
  return (
    <div className="ad-panel">
      {/* ── Top Header ── */}
      <div className="ad-panel-header">
        <div>
          <h2>👥 Platform Users</h2>
          <span className="ad-panel-sub">
            {pagination.totalUsers?.toLocaleString() || 0} total registered members · Comprehensive filtering & search
          </span>
        </div>
      </div>

      {/* ── Top Quick Stat Ribbon ── */}
      {stats && (
        <div className="ad-user-stats-ribbon">
          {[
            { icon: "👥", label: "Total Users", val: stats.totalUsers },
            { icon: "⭐", label: "Pro Members", val: stats.proUsers, highlight: true },
            { icon: "⚡", label: "Active Subs", val: stats.activeSubscribers },
            { icon: "🟢", label: "Active (7d)", val: stats.active7dUsers },
            { icon: "🏅", label: "Founding", val: stats.foundingUsers },
            { icon: "✓", label: "Verified", val: stats.verifiedUsers },
            { icon: "🛡️", label: "Admins", val: stats.adminCount },
          ].map((s) => (
            <div key={s.label} className={`ad-user-stat-card ${s.highlight ? "ad-user-stat-card--highlight" : ""}`}>
              <div className="ad-user-stat-icon">{s.icon}</div>
              <div className="ad-user-stat-content">
                <div className="ad-user-stat-val">{s.val?.toLocaleString() ?? 0}</div>
                <div className="ad-user-stat-label">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Advanced Filter Toolbar ── */}
      <div className="ad-user-filter-toolbar">
        <div className="ad-user-filter-row">
          {/* Search input */}
          <div className="ad-user-search-wrap">
            <span className="ad-user-search-icon">🔍</span>
            <input
              type="text"
              className="ad-input ad-user-search-input"
              placeholder="Search by name, email, or referral code..."
              value={filters.search}
              onChange={(e) => onFilterChange("search", e.target.value)}
            />
            {filters.search && (
              <button className="ad-clear-search-btn" onClick={() => onFilterChange("search", "")}>
                ✕
              </button>
            )}
          </div>

          {/* Paid / Plan Filter */}
          <select
            className="ad-select ad-user-filter-select"
            value={filters.isPaid}
            onChange={(e) => onFilterChange("isPaid", e.target.value)}
          >
            <option value="">All Tiers (Paid & Free)</option>
            <option value="true">⭐ Paid / PRO Only</option>
            <option value="false">🆓 Free Only</option>
          </select>

          {/* Subscription Status Filter */}
          <select
            className="ad-select ad-user-filter-select"
            value={filters.subscriptionStatus}
            onChange={(e) => onFilterChange("subscriptionStatus", e.target.value)}
          >
            <option value="">All Sub Statuses</option>
            <option value="ACTIVE">Active Subscriptions</option>
            <option value="INACTIVE">Inactive</option>
            <option value="CANCELLED">Cancelled</option>
            <option value="HALTED">Halted</option>
          </select>

          {/* Role Filter */}
          <select
            className="ad-select ad-user-filter-select"
            value={filters.role}
            onChange={(e) => onFilterChange("role", e.target.value)}
          >
            <option value="">All Roles</option>
            <option value="user">Regular Users</option>
            <option value="admin">🛡️ Admins</option>
          </select>

          {/* Verification Filter */}
          <select
            className="ad-select ad-user-filter-select"
            value={filters.isVerified}
            onChange={(e) => onFilterChange("isVerified", e.target.value)}
          >
            <option value="">All Verification</option>
            <option value="true">✓ Verified</option>
            <option value="false">Unverified</option>
          </select>

          {/* Founding Filter */}
          <select
            className="ad-select ad-user-filter-select"
            value={filters.isFoundingUser}
            onChange={(e) => onFilterChange("isFoundingUser", e.target.value)}
          >
            <option value="">All Memberships</option>
            <option value="true">🏅 Founding Members Only</option>
          </select>

          {/* Date Joined Presets */}
          <select
            className="ad-select ad-user-filter-select"
            value={filters.dateRange}
            onChange={(e) => onFilterChange("dateRange", e.target.value)}
          >
            <option value="all">Joined: All Time</option>
            <option value="7d">Joined: Past 7 Days</option>
            <option value="30d">Joined: Past 30 Days</option>
            <option value="90d">Joined: Past 90 Days</option>
          </select>

          {/* Sort By */}
          <select
            className="ad-select ad-user-filter-select"
            value={`${filters.sortBy}:${filters.sortOrder}`}
            onChange={(e) => {
              const [sortBy, sortOrder] = e.target.value.split(":");
              onFilterChange("sort", { sortBy, sortOrder });
            }}
          >
            <option value="createdAt:desc">📅 Joined: Newest First</option>
            <option value="createdAt:asc">📅 Joined: Oldest First</option>
            <option value="lastActiveAt:desc">⚡ Recently Active</option>
            <option value="xp:desc">🏆 Highest XP</option>
            <option value="credits:desc">🪙 Highest Credits</option>
            <option value="name:asc">🔤 Name (A → Z)</option>
            <option value="subscriptionEnd:desc">💳 Subscription Expiry</option>
          </select>

          {/* Page Limit */}
          <select
            className="ad-select ad-user-filter-select ad-user-filter-select--sm"
            value={filters.limit}
            onChange={(e) => onFilterChange("limit", Number(e.target.value))}
          >
            <option value={15}>15 / page</option>
            <option value={30}>30 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>

          {/* Reset Filters */}
          <button className="ad-btn ad-btn--ghost ad-btn--sm" onClick={onResetFilters} title="Reset all filters">
            ↻ Reset
          </button>
        </div>
      </div>

      {/* ── Table / User List ── */}
      {loading ? (
        <div className="ad-loading" style={{ minHeight: 300 }}>
          <div className="ad-spinner" />
          <p>Filtering & fetching users…</p>
        </div>
      ) : users.length === 0 ? (
        <div className="ad-empty">
          <p>No users matched your current search and filter criteria.</p>
          <button className="ad-btn ad-btn--accent ad-btn--sm" onClick={onResetFilters} style={{ marginTop: 12 }}>
            Clear Filters
          </button>
        </div>
      ) : (
        <div className="ad-user-table-wrap">
          <table className="ad-user-table">
            <thead>
              <tr>
                <th>User Details</th>
                <th>Plan & Subscription</th>
                <th>Role & Badges</th>
                <th>Gamification</th>
                <th>Activity Timeline</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isPro = u.subscription?.plan === "PRO";
                const subStatus = (u.subscription?.status || "INACTIVE").toLowerCase();

                return (
                  <tr key={u._id} className="ad-user-row">
                    {/* User Identity */}
                    <td className="ad-user-cell-profile">
                      <div className="ad-user-flex">
                        <div
                          className="ad-user-avatar"
                          style={{ backgroundColor: getAvatarColor(u.name || u.email) }}
                        >
                          {u.avatar ? (
                            <img src={u.avatar} alt={u.name} />
                          ) : (
                            (u.name?.[0] || u.email?.[0] || "U").toUpperCase()
                          )}
                        </div>
                        <div className="ad-user-text">
                          <div className="ad-user-name">{u.name}</div>
                          <div className="ad-user-email-row">
                            <span className="ad-user-email">{u.email}</span>
                            <button
                              className="ad-icon-btn-tiny"
                              onClick={() => onCopy(u.email, "Email")}
                              title="Copy email"
                            >
                              📋
                            </button>
                          </div>
                          {u.referralCode && (
                            <span className="ad-user-ref-code">Ref: {u.referralCode}</span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Subscription */}
                    <td>
                      <div className="ad-user-sub-cell">
                        <div className="ad-user-sub-top">
                          <span className={`ad-user-plan-badge ${isPro ? "ad-user-plan-badge--pro" : "ad-user-plan-badge--free"}`}>
                            {isPro ? "⭐ PRO" : "FREE"}
                          </span>
                          <span className={`ad-user-sub-status ad-user-sub-status--${subStatus}`}>
                            ● {u.subscription?.status || "INACTIVE"}
                          </span>
                        </div>
                        {u.subscription?.provider && (
                          <span className="ad-user-provider-tag">{u.subscription.provider}</span>
                        )}
                        {u.subscription?.currentPeriodEnd && (
                          <span className="ad-user-sub-expiry">
                            Ends: {fmtDate(u.subscription.currentPeriodEnd)}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Badges & Role */}
                    <td>
                      <div className="ad-user-badges-cell">
                        {u.role === "admin" && (
                          <span className="ad-user-badge ad-user-badge--admin">🛡️ Admin</span>
                        )}
                        {u.isFoundingUser && (
                          <span className="ad-user-badge ad-user-badge--founding">🏅 Founding</span>
                        )}
                        {u.isVerified ? (
                          <span className="ad-user-badge ad-user-badge--verified">✓ Verified</span>
                        ) : (
                          <span className="ad-user-badge ad-user-badge--unverified">Unverified</span>
                        )}
                        {u.googleId && (
                          <span className="ad-user-badge ad-user-badge--google" title="Google OAuth">🌐 Google</span>
                        )}
                      </div>
                    </td>

                    {/* Gamification */}
                    <td>
                      <div className="ad-user-game-cell">
                        <span className="ad-user-game-item"><strong>Lvl {u.level || 1}</strong></span>
                        <span className="ad-user-game-item">⚡ {(u.xp || 0).toLocaleString()} XP</span>
                        <span className="ad-user-game-item">🪙 {u.credits || 0}</span>
                      </div>
                    </td>

                    {/* Activity */}
                    <td>
                      <div className="ad-user-activity-cell">
                        <span className="ad-user-time-primary">
                          Active: <strong>{fmtRelativeTime(u.lastActiveAt)}</strong>
                        </span>
                        <span className="ad-user-time-sub">Joined {fmtDate(u.createdAt)}</span>
                      </div>
                    </td>

                    {/* Actions */}
                    <td>
                      <button
                        className="ad-btn ad-btn--sm ad-btn--accent"
                        onClick={() => onInspectUser(u._id)}
                      >
                        👁️ Inspect
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination Footer ── */}
      {pagination.totalPages > 1 && (
        <div className="ad-pagination-bar">
          <div className="ad-pagination-info">
            Showing {(pagination.page - 1) * pagination.limit + 1}–
            {Math.min(pagination.page * pagination.limit, pagination.totalUsers)} of{" "}
            {pagination.totalUsers.toLocaleString()} users
          </div>
          <div className="ad-pagination-controls">
            <button
              className="ad-btn ad-btn--ghost ad-btn--sm"
              disabled={!pagination.hasPrev}
              onClick={() => onPageChange(pagination.page - 1)}
            >
              ← Prev
            </button>
            <div className="ad-page-numbers">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let p = pagination.page - 2 + i;
                if (p < 1) p += (1 - p);
                if (p > pagination.totalPages) return null;
                return (
                  <button
                    key={p}
                    className={`ad-page-btn ${pagination.page === p ? "ad-page-btn--active" : ""}`}
                    onClick={() => onPageChange(p)}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
            <button
              className="ad-btn ad-btn--ghost ad-btn--sm"
              disabled={!pagination.hasNext}
              onClick={() => onPageChange(pagination.page + 1)}
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   § Main AdminDashboard
   ============================================================ */
export default function AdminDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  // JSON seeder
  const [seedJson, setSeedJson] = useState("");
  const [seedLoading, setSeedLoading] = useState(false);
  const [seedOptions, setSeedOptions] = useState({ quizzes: false, courses: true });

  // Visual editor
  const [editorMode, setEditorMode] = useState(null);
  const [editingTrack, setEditingTrack] = useState(null);
  const [editorSaving, setEditorSaving] = useState(false);

  // Duplicate modal
  const [duplicatingTrack, setDuplicatingTrack] = useState(null);

  // Track filters
  const [filters, setFilters] = useState({ category: "", goal: "", published: "", search: "" });

  // Section
  const [activeSection, setActiveSection] = useState("tracks");

  // Contribution filter
  const [conFilter, setConFilter] = useState("pending");

  // ── USER MANAGEMENT STATE ──────────────────────────────────
  const [userList, setUserList] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userFilters, setUserFilters] = useState({
    search: "",
    plan: "",
    subscriptionStatus: "",
    isPaid: "",
    role: "",
    isVerified: "",
    isFoundingUser: "",
    onboarding: "",
    dateRange: "all",
    sortBy: "createdAt",
    sortOrder: "desc",
    page: 1,
    limit: 20,
  });
  const [userPagination, setUserPagination] = useState({
    totalUsers: 0,
    page: 1,
    totalPages: 1,
    limit: 20,
    hasNext: false,
    hasPrev: false,
  });

  // User inspection modal
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);

  const flash = (type, text, ms = 4000) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), ms);
  };

  const copyToClipboard = (text, label = "Item") => {
    if (!text) return;
    navigator.clipboard?.writeText(text);
    flash("success", `📋 ${label} copied to clipboard!`, 2500);
  };

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filters.category) params.category = filters.category;
      if (filters.goal) params.goal = filters.goal;
      if (filters.published) params.published = filters.published;
      if (filters.search) params.search = filters.search;

      const [anaRes, conRes, traRes] = await Promise.all([
        ax("get", "/admin/analytics"),
        ax("get", `/admin/contributions?status=${conFilter}`),
        ax("get", "/admin/tracks", null, params),
      ]);
      setAnalytics(anaRes.data);
      setContributions(conRes.data);
      setTracks(traRes.data);
    } catch (err) {
      flash("error", "Failed to load admin data: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, conFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Fetch Users ──
  const fetchUsers = useCallback(async (customFilters = userFilters) => {
    setUsersLoading(true);
    try {
      const params = {};
      if (customFilters.search) params.search = customFilters.search;
      if (customFilters.plan) params.plan = customFilters.plan;
      if (customFilters.subscriptionStatus) params.subscriptionStatus = customFilters.subscriptionStatus;
      if (customFilters.isPaid) params.isPaid = customFilters.isPaid;
      if (customFilters.role) params.role = customFilters.role;
      if (customFilters.isVerified) params.isVerified = customFilters.isVerified;
      if (customFilters.isFoundingUser) params.isFoundingUser = customFilters.isFoundingUser;
      if (customFilters.onboarding) params.onboarding = customFilters.onboarding;
      if (customFilters.dateRange && customFilters.dateRange !== "all") params.dateRange = customFilters.dateRange;
      if (customFilters.sortBy) params.sortBy = customFilters.sortBy;
      if (customFilters.sortOrder) params.sortOrder = customFilters.sortOrder;
      params.page = customFilters.page || 1;
      params.limit = customFilters.limit || 20;

      const [usersRes, statsRes] = await Promise.all([
        ax("get", "/admin/users", null, params),
        ax("get", "/admin/users/stats"),
      ]);

      setUserList(usersRes.data.users || []);
      setUserPagination(usersRes.data.pagination || {});
      setUserStats(statsRes.data || null);
    } catch (err) {
      flash("error", "Failed to load users: " + err.message);
    } finally {
      setUsersLoading(false);
    }
  }, [userFilters]);

  useEffect(() => {
    if (activeSection === "users") {
      fetchUsers(userFilters);
    }
  }, [activeSection, userFilters, fetchUsers]);

  const handleUserFilterChange = (key, value) => {
    if (key === "sort") {
      setUserFilters((prev) => ({
        ...prev,
        sortBy: value.sortBy,
        sortOrder: value.sortOrder,
        page: 1,
      }));
    } else {
      setUserFilters((prev) => ({
        ...prev,
        [key]: value,
        page: 1,
      }));
    }
  };

  const handleResetUserFilters = () => {
    setUserFilters({
      search: "",
      plan: "",
      subscriptionStatus: "",
      isPaid: "",
      role: "",
      isVerified: "",
      isFoundingUser: "",
      onboarding: "",
      dateRange: "all",
      sortBy: "createdAt",
      sortOrder: "desc",
      page: 1,
      limit: 20,
    });
  };

  const handleUserPageChange = (newPage) => {
    setUserFilters((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleInspectUser = async (userId) => {
    setSelectedUserId(userId);
    setSelectedUserDetails(null);
    setUserDetailsLoading(true);
    try {
      const res = await ax("get", `/admin/users/${userId}`);
      setSelectedUserDetails(res.data);
    } catch (err) {
      flash("error", "Failed to load user details: " + err.message);
      setSelectedUserId(null);
    } finally {
      setUserDetailsLoading(false);
    }
  };

  /* ── JSON bulk seed ── */
  const handleSeed = async () => {
    setSeedLoading(true);
    try {
      const data = JSON.parse(seedJson);
      const params = {};
      if (seedOptions.quizzes) params.quizzes = "1";
      if (!seedOptions.courses) params.courses = "0";

      const res = await ax("post", "/admin/tracks/seed-bulk", data, params);
      const r = res.data.results;
      let msg = `✅ Created: ${r?.created ?? "?"}, Updated: ${r?.updated ?? "?"}`;
      if (r?.courseLinks?.length) msg += `, Courses linked: ${r.courseLinks.filter((l) => l.status === "linked").length}`;
      if (r?.quizResults?.length) msg += `, Quizzes: ${JSON.stringify(r.quizResults)}`;
      if (r?.errors?.length) msg += ` ⚠️ Errors: ${r.errors.join("; ")}`;
      flash("success", msg, 8000);
      setSeedJson("");
      fetchData();
    } catch (err) {
      flash("error", "JSON error or server error: " + err.message);
    } finally {
      setSeedLoading(false);
    }
  };

  /* ── Load track JSON into seed editor ── */
  const handleEditJson = async (slug) => {
    try {
      const res = await ax("get", `/admin/tracks/${slug}/json`);
      setSeedJson(JSON.stringify([res.data], null, 2));
      setActiveSection("seed");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      flash("error", "Failed to fetch track JSON: " + err.message);
    }
  };

  /* ── Visual editor ── */
  const handleEditVisual = async (slug) => {
    try {
      const res = await ax("get", `/admin/tracks/${slug}/json`);
      setEditingTrack(res.data);
      setEditorMode("edit");
    } catch (err) {
      flash("error", "Failed to load track: " + err.message);
    }
  };

  const handleNewTrack = () => { setEditingTrack(null); setEditorMode("new"); };

  const handleEditorSave = async (payload) => {
    setEditorSaving(true);
    try {
      const res = await ax("post", "/admin/tracks/seed-bulk", [payload]);
      flash("success", `✅ Track saved! Created: ${res.data.results?.created ?? 0}, Updated: ${res.data.results?.updated ?? 0}`);
      setEditorMode(null);
      setEditingTrack(null);
      fetchData();
    } catch (err) {
      flash("error", "Save failed: " + err.message);
    } finally {
      setEditorSaving(false);
    }
  };

  /* ── Delete track ── */
  const handleDelete = async (track) => {
    if (!window.confirm(`Delete "${track.title}"? This cannot be undone.`)) return;
    try {
      await ax("delete", `/admin/tracks/${track.slug}`);
      flash("success", `Track "${track.title}" deleted.`);
      fetchData();
    } catch (err) {
      flash("error", "Delete failed: " + err.message);
    }
  };

  /* ── Duplicate track ── */
  const handleDuplicate = async (newSlug) => {
    try {
      await ax("post", `/admin/tracks/${duplicatingTrack.slug}/duplicate`, { newSlug });
      flash("success", `Track duplicated as "${newSlug}"!`);
      setDuplicatingTrack(null);
      fetchData();
    } catch (err) {
      flash("error", "Duplicate failed: " + err.message);
      setDuplicatingTrack(null);
    }
  };

  /* ── Toggle publish ── */
  const handleTogglePublish = async (track) => {
    try {
      await ax("patch", `/admin/tracks/${track.slug}/publish`);
      flash("success", `Track ${!track.isPublished ? "published" : "unpublished"}!`);
      fetchData();
    } catch (err) {
      flash("error", "Toggle failed: " + err.message);
    }
  };

  /* ── Generate quizzes from track list ── */
  const handleGenerateQuizzesForTrack = async (slug) => {
    try {
      flash("success", `🤖 Generating quizzes for ${slug}...`, 60000);
      const res = await ax("post", `/admin/tracks/${slug}/generate-quizzes`, {});
      flash("success", `✅ Quizzes done for ${slug}: ${JSON.stringify(res.data.results)}`);
      fetchData();
    } catch (err) {
      flash("error", "Quiz gen failed: " + err.message);
    }
  };

  /* ── Contribution review ── */
  const handleReview = async (id, status) => {
    try {
      await ax("put", `/admin/contributions/${id}/${status}`, {});
      flash("success", `Contribution ${status}d.`);
      fetchData();
    } catch (err) {
      flash("error", "Review failed: " + err.message);
    }
  };

  /* ── Full-screen editor ── */
  if (editorMode) {
    return (
      <div className="ad-page">
        {message && <div className={`ad-toast ad-toast--${message.type}`}>{message.text}</div>}
        <TrackEditor
          initial={editingTrack}
          tracks={tracks}
          onSave={handleEditorSave}
          onCancel={() => { setEditorMode(null); setEditingTrack(null); }}
          saving={editorSaving}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="ad-page">
        <div className="ad-loading">
          <div className="ad-spinner" />
          <p>Loading admin data…</p>
        </div>
      </div>
    );
  }

  const publishedTracks = tracks.filter((t) => t.isPublished).length;

  return (
    <div className="ad-page">
      {message && <div className={`ad-toast ad-toast--${message.type}`}>{message.text}</div>}
      
      {duplicatingTrack && (
        <DuplicateModal
          track={duplicatingTrack}
          onConfirm={handleDuplicate}
          onClose={() => setDuplicatingTrack(null)}
        />
      )}

      {selectedUserId && (
        <UserDetailsModal
          userId={selectedUserId}
          details={selectedUserDetails}
          loading={userDetailsLoading}
          onClose={() => {
            setSelectedUserId(null);
            setSelectedUserDetails(null);
          }}
          onCopy={copyToClipboard}
        />
      )}

      {/* ── Header ── */}
      <div className="ad-header">
        <div>
          <h1 className="ad-header-title">🛡️ Learnlia Admin</h1>
          <p className="ad-header-sub">User management · Track management · Seeding · Quizzes · Contributions</p>
        </div>
        <button className="ad-btn ad-btn--primary" onClick={handleNewTrack}>+ New Track</button>
      </div>

      {/* ── Quick Stats ── */}
      {analytics && (
        <div className="ad-stats-row">
          {[
            { icon: "👥", label: "Users", val: analytics.summary.totalUsers },
            { icon: "📚", label: "Tracks", val: analytics.summary.totalTracks },
            { icon: "✅", label: "Published", val: publishedTracks },
            { icon: "🎬", label: "Courses", val: analytics.summary.totalCourses },
            { icon: "🧠", label: "Quizzes", val: analytics.summary.totalQuizzes },
            { icon: "💬", label: "Pending", val: analytics.summary.pendingContributions, alert: analytics.summary.pendingContributions > 0 },
          ].map((s) => (
            <div key={s.label} className={`ad-stat-chip ${s.alert ? "ad-stat-chip--alert" : ""}`}>
              <span className="ad-stat-chip-icon">{s.icon}</span>
              <span className="ad-stat-chip-val">{s.val}</span>
              <span className="ad-stat-chip-label">{s.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Nav ── */}
      <div className="ad-nav">
        {[
          { key: "users", label: `👥 Users (${userStats?.totalUsers ?? analytics?.summary?.totalUsers ?? "..."})` },
          { key: "tracks", label: `📋 Tracks (${tracks.length})` },
          { key: "analytics", label: "📊 Analytics" },
          { key: "seed", label: "🌱 JSON Seeder" },
          { key: "contributions", label: `💬 Contributions`, badge: analytics?.summary?.pendingContributions },
        ].map((n) => (
          <button key={n.key}
            className={`ad-nav-btn ${activeSection === n.key ? "ad-nav-btn--active" : ""}`}
            onClick={() => setActiveSection(n.key)}>
            {n.label}
            {n.badge > 0 && <span className="ad-nav-badge">{n.badge}</span>}
          </button>
        ))}
      </div>

      {/* ── USERS PANEL ── */}
      {activeSection === "users" && (
        <UsersPanel
          users={userList}
          stats={userStats}
          loading={usersLoading}
          filters={userFilters}
          pagination={userPagination}
          onFilterChange={handleUserFilterChange}
          onResetFilters={handleResetUserFilters}
          onPageChange={handleUserPageChange}
          onInspectUser={handleInspectUser}
          onCopy={copyToClipboard}
        />
      )}

      {/* ── TRACKS PANEL ── */}
      {activeSection === "tracks" && (
        <div className="ad-panel">
          <div className="ad-panel-header">
            <h2>All Tracks</h2>
            <span className="ad-panel-sub">{publishedTracks} published · {tracks.length - publishedTracks} drafts</span>
          </div>

          {/* Filters */}
          <div className="ad-track-filters">
            <input className="ad-input ad-filter-search" placeholder="🔍 Search tracks..."
              value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} />
            <select className="ad-select ad-filter-select" value={filters.category}
              onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}>
              <option value="">All Categories</option>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select className="ad-select ad-filter-select" value={filters.goal}
              onChange={(e) => setFilters((f) => ({ ...f, goal: e.target.value }))}>
              <option value="">All Goals</option>
              {GOALS.map((g) => <option key={g}>{g}</option>)}
            </select>
            <select className="ad-select ad-filter-select" value={filters.published}
              onChange={(e) => setFilters((f) => ({ ...f, published: e.target.value }))}>
              <option value="">All Status</option>
              <option value="true">Published</option>
              <option value="false">Draft</option>
            </select>
            <button className="ad-btn ad-btn--ghost ad-btn--sm" onClick={() => setFilters({ category: "", goal: "", published: "", search: "" })}>
              Clear
            </button>
          </div>

          <div className="ad-track-list">
            {tracks.length === 0 && <div className="ad-empty">No tracks match your filters.</div>}
            {tracks.map((track) => (
              <div key={track._id} className="ad-track-row">
                <div className="ad-track-thumb">
                  {track.thumbnail
                    ? <img src={track.thumbnail} alt="" />
                    : <div className="ad-track-thumb-placeholder">{track.category?.[0]?.toUpperCase() || "T"}</div>
                  }
                </div>
                <div className="ad-track-info">
                  <div className="ad-track-title">{track.title}</div>
                  <div className="ad-track-meta">
                    <span className="ad-pill ad-pill--cat">{track.category}</span>
                    <span className="ad-pill ad-pill--goal">{track.goal}</span>
                    {track.isPro && <span className="ad-pill ad-pill--pro">PRO</span>}
                    <span className="ad-pill-neutral">{track.phaseCount} phases</span>
                    <span className="ad-pill-neutral">👥 {track.enrollmentCount}</span>
                    {track.phasesWithAssessments > 0 && (
                      <span className={`ad-pill ${track.quizCoveragePercent === 100 ? "ad-pill--quiz-ok" : "ad-pill--quiz-partial"}`}>
                        🧠 {track.quizCoveragePercent}%
                      </span>
                    )}
                    <span className="ad-track-slug">/{track.slug}</span>
                  </div>
                </div>
                <div className="ad-track-actions">
                  <button
                    className={`ad-publish-toggle ${track.isPublished ? "ad-publish-toggle--on" : "ad-publish-toggle--off"}`}
                    onClick={() => handleTogglePublish(track)}>
                    {track.isPublished ? "● Live" : "○ Draft"}
                  </button>
                  <button className="ad-btn ad-btn--sm ad-btn--accent" onClick={() => handleEditVisual(track.slug)}>
                    ✏️ Edit
                  </button>
                  {track.phasesWithAssessments > 0 && track.quizCoveragePercent < 100 && (
                    <button className="ad-btn ad-btn--sm ad-btn--ghost" title="Generate quizzes"
                      onClick={() => handleGenerateQuizzesForTrack(track.slug)}>
                      🤖
                    </button>
                  )}
                  <button className="ad-btn ad-btn--sm ad-btn--ghost" onClick={() => setDuplicatingTrack(track)} title="Duplicate">
                    🔁
                  </button>
                  <button className="ad-btn ad-btn--sm ad-btn--ghost" onClick={() => handleEditJson(track.slug)} title="Load JSON">
                    { }JSON
                  </button>
                  <button className="ad-btn ad-btn--sm ad-btn--danger-ghost" onClick={() => handleDelete(track)} title="Delete">
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── ANALYTICS PANEL ── */}
      {activeSection === "analytics" && (
        <div className="ad-panel">
          <div className="ad-panel-header">
            <h2>📊 Platform Analytics</h2>
            <button className="ad-btn ad-btn--ghost ad-btn--sm" onClick={fetchData}>↻ Refresh</button>
          </div>
          <div style={{ padding: "20px 22px" }}>
            <AnalyticsPanel analytics={analytics} />
          </div>
        </div>
      )}

      {/* ── JSON SEEDER PANEL ── */}
      {activeSection === "seed" && (
        <div className="ad-panel">
          <div className="ad-panel-header">
            <h2>🌱 JSON Bulk Seeder</h2>
            <span className="ad-panel-sub">Paste a JSON array of tracks. Slug = unique key. Safe to re-run.</span>
          </div>

          <div className="ad-seed-layout">
            <div className="ad-seed-main">
              {/* Seed options */}
              <div className="ad-seed-options">
                <label className="ad-seed-option">
                  <input type="checkbox" checked={seedOptions.courses}
                    onChange={(e) => setSeedOptions((o) => ({ ...o, courses: e.target.checked }))} />
                  <span>Auto-link playlist → Course</span>
                </label>
                <label className="ad-seed-option">
                  <input type="checkbox" checked={seedOptions.quizzes}
                    onChange={(e) => setSeedOptions((o) => ({ ...o, quizzes: e.target.checked }))} />
                  <span>Auto-generate quizzes <span className="ad-seed-option-warn">(slow — needs quiz service)</span></span>
                </label>
              </div>

              <div className="ad-seed-toolbar">
                <button className="ad-btn ad-btn--ghost ad-btn--sm"
                  onClick={() => setSeedJson(JSON.stringify([EMPTY_TRACK()], null, 2))}>
                  Insert template
                </button>
                <button className="ad-btn ad-btn--ghost ad-btn--sm" onClick={() => setSeedJson("")} disabled={!seedJson}>
                  Clear
                </button>
                <span className="ad-char-count">{seedJson.length} chars</span>
              </div>

              <textarea className="ad-seed-textarea" value={seedJson}
                onChange={(e) => setSeedJson(e.target.value)}
                placeholder={`[\n  {\n    "title": "My Track",\n    "slug": "my-track",\n    ...\n  }\n]`}
                spellCheck={false} />

              <div className="ad-seed-footer">
                <button className="ad-btn ad-btn--primary" onClick={handleSeed}
                  disabled={seedLoading || !seedJson.trim()}>
                  {seedLoading ? "⏳ Running…" : "🚀 Run Bulk Seed"}
                </button>
              </div>
            </div>

            <div className="ad-seed-sidebar">
              <div className="ad-seed-hint">
                <strong>How to use</strong>
                <ol>
                  <li>Paste a JSON array of track objects.</li>
                  <li>Each object needs a unique <code>slug</code>.</li>
                  <li>Existing slugs are updated (upsert).</li>
                  <li>Toggle options above to auto-link courses and generate quizzes.</li>
                </ol>
              </div>
              <div className="ad-seed-hint">
                <strong>Auto-link Courses</strong>
                <p>Any resource with <code>type: "playlist"</code> gets its YouTube playlist imported and linked to a Course document automatically. <code>courseId</code> is set on the resource.</p>
              </div>
              <div className="ad-seed-hint">
                <strong>Auto-generate Quizzes</strong>
                <p>Calls your quiz microservice for each video in <code>assessmentVideoIds</code>. Requires the HF Space to be deployed and <code>QUIZ_SERVICE_URL</code> set.</p>
              </div>
              <div className="ad-seed-hint">
                <strong>Tip</strong>
                <p>Use <button className="ad-link-btn" onClick={handleNewTrack}>visual editor</button> for a guided form-based experience.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── CONTRIBUTIONS PANEL ── */}
      {activeSection === "contributions" && (
        <div className="ad-panel">
          <div className="ad-panel-header">
            <h2>💬 Community Contributions</h2>
            <div style={{ display: "flex", gap: 8 }}>
              {["pending", "approved", "rejected", ""].map((s) => (
                <button key={s}
                  className={`ad-btn ad-btn--sm ${conFilter === s ? "ad-btn--primary" : "ad-btn--ghost"}`}
                  onClick={() => setConFilter(s)}>
                  {s || "All"}
                </button>
              ))}
            </div>
          </div>

          {contributions.length === 0 ? (
            <div className="ad-empty">🎉 No contributions matching filter!</div>
          ) : (
            <div className="ad-contribution-list">
              {contributions.map((con) => (
                <div key={con._id} className={`ad-con-row ad-con-row--${con.status}`}>
                  <div className="ad-con-status-bar" />
                  <div className="ad-con-body">
                    <div className="ad-con-top">
                      <strong className="ad-con-title">{con.resourceData?.title}</strong>
                      <span className={`ad-pill ad-pill--status-${con.status}`}>{con.status}</span>
                    </div>
                    <div className="ad-con-meta">
                      by <strong>{con.user?.name || "Anonymous"}</strong>{" "}for{" "}
                      <strong>{con.track?.title || "Unknown Track"}</strong>
                    </div>
                    {con.resourceData?.url && (
                      <a href={con.resourceData.url} target="_blank" rel="noreferrer" className="ad-con-link">
                        {con.resourceData.url.slice(0, 60)}… ↗
                      </a>
                    )}
                    {con.suggestionIdea && <p className="ad-con-idea">"{con.suggestionIdea}"</p>}
                  </div>
                  {con.status === "pending" && (
                    <div className="ad-con-actions">
                      <button className="ad-btn ad-btn--approve" onClick={() => handleReview(con._id, "approve")}>✓ Approve</button>
                      <button className="ad-btn ad-btn--reject" onClick={() => handleReview(con._id, "reject")}>✗ Reject</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}